import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Check, Shield, FileText, Users, Wallet } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PUBLIC_PLANS_API = `${BACKEND_URL}/api/public/frontoffice-plans`;

// V38.04 — Fallback hardcodé identique au seed (zéro régression si API KO).
const DEFAULT_COPPAM = {
  price: 29,
  period: '/mois',
  credits_per_month: 30,
  description: "Accédez aux simulateurs et documents liés aux dispositifs solidaires COPPAM, sans logique assurantielle.",
  benefits: [
    'Simulations standards COPPAM',
    "Génération d'attestations solidaires",
    'Espace documentaire (GED)',
    'Historique des simulations',
    'Support par email',
    'Conformité ESS garantie',
  ],
  cta_label: 'Adhérer & activer mon wallet',
  cta_url: '/inscription-ess?plan=adherent',
};

const OffreCOPPAM = () => {
  const [plan, setPlan] = useState(DEFAULT_COPPAM);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(PUBLIC_PLANS_API);
        const coppam = (res.data?.plans || []).find((p) => p.code === 'coppam_monthly');
        if (!coppam || cancelled) return;
        setPlan({
          price: coppam.price,
          period: coppam.period || '/mois',
          credits_per_month: coppam.credits_per_month || 0,
          description: coppam.description || DEFAULT_COPPAM.description,
          benefits: coppam.benefits || DEFAULT_COPPAM.benefits,
          cta_label: coppam.cta_label || DEFAULT_COPPAM.cta_label,
          cta_url: coppam.cta_url || DEFAULT_COPPAM.cta_url,
        });
      } catch { /* fallback silencieux */ }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 50%, #f0fdfa 100%)' }}>
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-emerald-100">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-600 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <span className="font-bold text-xl text-emerald-800">COPPAM</span>
          </div>
          <nav className="flex items-center gap-6">
            <Link to="/offre-oscop" className="text-slate-600 hover:text-emerald-600 transition">O'SCOP Pro</Link>
            <Link to="/offre-fogedom" className="text-slate-600 hover:text-emerald-600 transition">FOGEDOM</Link>
            <Link to="/login" className="bg-emerald-600 text-white px-5 py-2.5 rounded-xl font-medium hover:bg-emerald-700 transition">
              Connexion
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Users className="w-4 h-4" />
            Offre ESS – Artisans & TPE
          </span>
          <h1 className="text-5xl font-bold text-slate-800 mb-4">
            Adhésion <span className="text-emerald-600">COPPAM</span>
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            {plan.description}
          </p>
        </div>

        {/* Main Card */}
        <div className="max-w-xl mx-auto">
          <div className="bg-white rounded-3xl shadow-2xl shadow-emerald-200/50 overflow-hidden">
            {/* Price Header */}
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-8 text-center">
              <div className="text-emerald-100 text-lg mb-2">À partir de</div>
              <div className="flex items-baseline justify-center gap-1">
                <span className="text-6xl font-bold text-white">{plan.price}</span>
                <span className="text-2xl text-emerald-100">€ {plan.period}</span>
              </div>
              {plan.credits_per_month > 0 && (
                <div className="mt-4 inline-flex items-center gap-2 bg-white/20 backdrop-blur px-4 py-2 rounded-full">
                  <Wallet className="w-5 h-5 text-white" />
                  <span className="text-white font-medium">{plan.credits_per_month} crédits inclus / mois</span>
                </div>
              )}
            </div>

            {/* Features */}
            <div className="p-8">
              <h3 className="font-semibold text-slate-800 mb-4">Inclus dans votre adhésion :</h3>
              <ul className="space-y-4">
                {plan.benefits.map((feature, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-emerald-600" />
                    </div>
                    <span className="text-slate-700">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <Link 
                to={plan.cta_url}
                className="mt-8 w-full bg-emerald-600 hover:bg-emerald-700 text-white py-4 px-6 rounded-2xl font-semibold text-lg flex items-center justify-center gap-2 transition-all hover:shadow-lg hover:shadow-emerald-300/50"
              >
                <Shield className="w-5 h-5" />
                {plan.cta_label}
              </Link>
            </div>
          </div>
        </div>

        {/* Credits Info */}
        <div className="max-w-3xl mx-auto mt-12 bg-white/60 backdrop-blur rounded-2xl p-6 border border-emerald-100">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <FileText className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <h4 className="font-semibold text-slate-800 mb-1">Comment fonctionnent les crédits ?</h4>
              <p className="text-slate-600 text-sm">
                Les crédits permettent l'activation des simulateurs et la génération de documents.
                Chaque simulation standard consomme 2 crédits, une génération PDF 2 crédits.
                Des crédits supplémentaires peuvent être acquis à tout moment selon l'usage.
              </p>
            </div>
          </div>
        </div>

        {/* Legal Note */}
        <div className="text-center mt-8">
          <p className="text-sm text-slate-500 max-w-2xl mx-auto">
            <strong>Mention légale :</strong> COPPAM propose un service numérique d'accompagnement 
            aux dispositifs solidaires ESS. Aucune activité d'assurance – aucun transfert de risque.
          </p>
        </div>
      </div>
    </div>
  );
};

export default OffreCOPPAM;
